import { addOrUpdateCropsBauData, addOrUpdateFertilizerBauData, addOrUpdatePesticidesBauData, addOrUpdateTillageSeedingBauData } from "services";

import { getIsBeforeDeadline, getIsLate } from "utils";

import {
  BauEntry,
  CropEntry,
  EntriesToUpdate,
  FertilizerEntry,
  FieldModuleStatus,
  FieldModuleStatusValues,
  FullFieldModule,
  Module,
  PesticideEntry,
  TillageSeedingEntry,
  YearlyEntry,
} from "models";

export type GetResiduePayoutPotentialRequest = { cropId: string; hectares: number; regionId?: string; fieldId?: string };

export type AddOrUpdateBauEntryRequest = {
  id: string | undefined;
  fieldId: string;
  fieldModuleId: string;
  moduleId: string;
  bauEntries: EntriesToUpdate;
  entriesToDelete: EntriesToDelete;
  signal: AbortSignal;
};

type BauEntrySaveFunctionFactoryParams = {
  bauEntries: Entries;
  confirmed?: boolean;
  entriesToDelete: EntriesToDelete;
  fieldModule: FullFieldModule;
  moduleSlug: FieldModuleSlug;
  signal: AbortSignal;
  year: number;
  onFinish: (options: { confirmed: boolean; updatedEntries: Entries; progress: number }) => void;
  onSuccess: () => void;
};

// TODO: remove unknowns
type Entries = {
  crops: {
    [year: number]: CropEntry[];
  };
  fertilizer: {
    [year: number]: FertilizerEntry[];
  };
  tillage_seeding: {
    [year: number]: TillageSeedingEntry[];
  };
  pesticides: {
    [year: number]: PesticideEntry[];
  };
};

export type EntriesToDelete = {
  crops: string[];
  fertilizer: string[];
  tillage_seeding: string[];
  pesticides: string[];
};

type FieldModuleSlug = "crops" | "fertilizer" | "pesticides" | "tillage_seeding";
type ModuleSlug = FieldModuleSlug;

type GetFieldModuleStatusParams = {
  bauEntry: BauEntry;
  yearlyEntries: YearlyEntry[];
  updateRequired: boolean;
  currentYearlyDataYear: number;
};

type BauSaveFunctionParams = { key: FieldModuleSlug; data: unknown; emptyYearsData: [] };
type BauSaveFunction = ({ key, data, emptyYearsData }: BauSaveFunctionParams) => void;

export const getFieldModuleStatus = ({ bauEntry, yearlyEntries, updateRequired, currentYearlyDataYear }: GetFieldModuleStatusParams): FieldModuleStatus => {
  const isBeforeDeadline = getIsBeforeDeadline(currentYearlyDataYear);
  const isLate = getIsLate(currentYearlyDataYear);

  if (!bauEntry?.confirmed) {
    if (isBeforeDeadline) {
      return {
        progress: bauEntry?.progress || 0,
        status: FieldModuleStatusValues.BEFORE_DEADLINE_BASELINE,
      };
    }

    if (isLate) {
      return {
        progress: bauEntry?.progress || 0,
        status: FieldModuleStatusValues.LATE_BASELINE,
      };
    }
  }

  if (!bauEntry) {
    return {
      progress: 0,
      status: FieldModuleStatusValues.BAU_PENDING,
    };
  }

  if (bauEntry.confirmed) {
    const currentYearlyEntry = yearlyEntries?.length > 0 ? yearlyEntries.find((entry) => entry.year === currentYearlyDataYear) : null;

    if (!currentYearlyEntry?.confirmed) {
      if (isBeforeDeadline) {
        return {
          progress: currentYearlyEntry ? 100 : 0,
          status: FieldModuleStatusValues.BEFORE_DEADLINE_CURRENT_YEAR,
        };
      }

      if (isLate) {
        return {
          progress: currentYearlyEntry ? 100 : 0,
          status: FieldModuleStatusValues.LATE_CURRENT_YEAR,
        };
      }
    }

    if (currentYearlyEntry) {
      if (currentYearlyEntry.confirmed) {
        return {
          progress: 100,
          status: FieldModuleStatusValues.IN_VERIFICATION,
        };
      }

      if (!updateRequired) {
        return {
          progress: 100,
          status: FieldModuleStatusValues.REVIEW_CURRENT_YEAR,
        };
      }
    }

    return {
      progress: bauEntry.progress,
      status: FieldModuleStatusValues.ACTIVE,
    };
  }

  return {
    progress: bauEntry.progress,
    status: bauEntry.progress === 100 && !updateRequired ? FieldModuleStatusValues.REVIEW_BASELINE : FieldModuleStatusValues.BAU_PENDING,
  };
};

export const sortByModulePriority =
  (key = null) =>
  (a: Module | ModuleSlug) => {
    const moduleSlug = !key ? a : a[key];

    if (moduleSlug === "pesticides") {
      return 1;
    }

    if (moduleSlug === "tillage_seeding") {
      return 1;
    }

    if (moduleSlug === "fertilizer") {
      return 1;
    }

    return -1;
  };

export const createBlankCrop = ({ isMain }: { isMain: boolean }) => ({
  cropId: null,
  residue: null,
  mainCrop: isMain,
  mandatory: null,
  redzoneExceptionId: null,
  key: Date.now(),
});

const defaultEntriesToDelete = {
  crops: [],
  fertilizer: [],
  tillage_seeding: [],
  pesticides: [],
};

export const bauEntrySaveFunctionFactory = ({
  bauEntries,
  confirmed = false,
  entriesToDelete = defaultEntriesToDelete,
  fieldModule,
  moduleSlug,
  signal,
  year,
  onFinish,
  onSuccess,
}: BauEntrySaveFunctionFactoryParams): BauSaveFunction => {
  const completeTillageSeedingForm = (updatedEntries: Entries) => {
    addOrUpdateTillageSeedingBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.module.id,
      bauEntries: {
        tillage_seeding: updatedEntries.tillage_seeding,
        crops: {},
        fertilizer: {},
        pesticides: {},
      },
      entriesToDelete,
      signal,
    })
      .then(() => {
        onSuccess?.();
      })
      .finally(() => {
        onFinish?.({ confirmed, updatedEntries, progress: (Object.keys(updatedEntries.tillage_seeding).length / 3) * 100 });
      });
  };

  const completeFertilizerForm = (updatedEntries: Entries) => {
    addOrUpdateFertilizerBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.module.id,
      bauEntries: {
        fertilizer: updatedEntries.fertilizer,
        crops: {},
        tillage_seeding: {},
        pesticides: {},
      },
      entriesToDelete,
      signal,
    })
      .then(() => {
        onSuccess?.();
      })
      .finally(() => {
        onFinish?.({ confirmed, updatedEntries, progress: (Object.keys(updatedEntries.fertilizer).length / 3) * 100 });
      });
  };

  const completePesticidesForm = (updatedEntries: Entries) => {
    addOrUpdatePesticidesBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.module.id,
      bauEntries: {
        pesticides: updatedEntries.pesticides,
        crops: {},
        fertilizer: {},
        tillage_seeding: {},
      },
      entriesToDelete,
      signal,
    })
      .then(() => {
        onSuccess?.();
      })
      .finally(() => {
        onFinish?.({ confirmed, updatedEntries, progress: (Object.keys(updatedEntries.pesticides).length / 3) * 100 });
      });
  };

  const completeCropsForm = (updatedEntries: Entries) => {
    addOrUpdateCropsBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.module.id,
      bauEntries: {
        crops: updatedEntries.crops,
        fertilizer: {},
        tillage_seeding: {},
        pesticides: {},
      },
      entriesToDelete,
      signal,
    })
      .then(() => {
        onSuccess?.();
      })
      .finally(() => {
        onFinish?.({ confirmed, updatedEntries, progress: (Object.keys(updatedEntries.crops).length / 3) * 100 });
      });
  };

  return ({ key, data, emptyYearsData = [] }: BauSaveFunctionParams) => {
    const updatedEntries = year
      ? {
          ...bauEntries,
          [key]: {
            ...bauEntries[key],
            [year]: data,
            ...emptyYearsData,
          },
        }
      : {
          ...bauEntries,
          [key]: data,
        };

    if (moduleSlug === "fertilizer") {
      completeFertilizerForm(updatedEntries);
    }

    if (moduleSlug === "crops") {
      completeCropsForm(updatedEntries);
    }

    if (moduleSlug === "pesticides") {
      completePesticidesForm(updatedEntries);
    }

    if (moduleSlug === "tillage_seeding") {
      completeTillageSeedingForm(updatedEntries);
    }
  };
};
